import { signal, computed } from "@preact/signals";
import { createLegacyOrderItemSignal } from "./orderItem";

export function createLegacyOrderSignal(data, configs = {}) {
  const notes = signal("");

  const tip = signal(0);

  const pickupChannelId = signal("");

  const items = signal(
    (data?.items || []).map((item) => {
      return createLegacyOrderItemSignal({
        ...item,
        taxes: item.taxes || data?.taxes, // attach order level taxes if not on item level
      });
    }),
  );

  const order = computed(() => {
    let discountTotal = 0;
    let feeTotal = 0;
    let taxTotal = 0;
    let subTotal = 0;

    (items?.value || []).forEach((item) => {
      const orderItem = item?.orderItem?.value;
      discountTotal -= orderItem?.discount; // order discount for SALEs is always negative; item discount is always positive (it has no sign)
      feeTotal += orderItem?.fee;
      taxTotal += (orderItem?.taxes || []).reduce((p, c) => {
        return p + c.amount / Math.pow(10, (c.amountPrecision || 2) - 2);
      }, 0);
      subTotal += orderItem?.quantity * orderItem?.unitPrice;
    });

    // round this since we did a bunch of floating point math earlier
    taxTotal = Math.round(taxTotal);

    // checks if calculations are wrong; if wrong, default to order amounts
    // We just do this for Invoice Pay Links!
    if (data?.amounts && configs?.isInvoicePayLink) {
      if (taxTotal !== data.amounts.taxTotal) {
        console.warn(
          "Tax total calculated from order items does not match tax total from order amounts",
          taxTotal,
          data.amounts.taxTotal,
        );
        taxTotal = data.amounts.taxTotal;
      }

      if (feeTotal !== data.amounts.feeTotal) {
        console.warn(
          "Fee total calculated from order items does not match fee total from order amounts",
          feeTotal,
          data.amounts.feeTotal,
        );
        feeTotal = data.amounts.feeTotal;
      }

      if (discountTotal !== data.amounts.discountTotal) {
        console.warn(
          "Discount total calculated from order items does not match discount total from order amounts",
          discountTotal,
          data.amounts.discountTotal,
        );
        discountTotal = data.amounts.discountTotal;
      }

      if (subTotal !== data.amounts.subTotal) {
        console.warn(
          "Subtotal calculated from order items does not match subtotal from order amounts",
          subTotal,
          data.amounts.subTotal,
        );
        subTotal = data.amounts.subTotal;
      }
    }

    let netTotal = subTotal + feeTotal + discountTotal + taxTotal;

    // Also validate netTotal if provided in data.amounts
    // We just do this for Invoice Pay Links!
    if (configs?.isInvoicePayLink && data?.amounts?.netTotal !== undefined && netTotal !== data.amounts.netTotal) {
      console.warn(
        "Net total calculated from order amounts does not match net total from order amounts",
        netTotal,
        data.amounts.netTotal,
      );
      netTotal = data.amounts.netTotal;
    }

    const orderObject = {
      context: {
        transactionInstruction: data?.context?.transactionInstruction || "NONE",
        source: data?.context?.source || "WEB",
        sourceApp: data?.context?.sourceApp || "",
        channelId: data?.context?.channelId || "",
      },
      statuses: {
        fulfillmentStatus: data?.statuses?.fulfillmentStatus || "FULFILLED",
        status: data?.statuses?.status || "OPENED",
        transactionStatusSummary: data?.statuses?.transactionStatusSummary || "PENDING",
      },
      amounts: {
        discountTotal,
        feeTotal,
        taxTotal,
        subTotal,
        netTotal,
        orderAmount: netTotal,
        tipAmount: tip.value,
        transactionAmount: netTotal + tip.value,
        currency: data?.currency || "USD",
      },
      customer: data?.customer,
      notes: notes?.value,
      taxes: data?.taxes,
      discounts: data?.discounts,
      fees: data?.fees,
      items: (items?.value || []).map((item) => {
        return item?.orderItem?.value;
      }),
    };

    // if order is a pickup order, set order status to open
    if (pickupChannelId) {
      orderObject.orderShipments = [
        {
          deliveryMode: "PICKUP",
          shipmentType: "FULFILLMENT",
          status: "AWAITING_PICKUP",
        },
      ];

      orderObject.statuses.fulfillmentStatus = "NONE";

      orderObject.items.forEach((item) => {
        if (!item.pickupChannelId) {
          item.pickupChannelId = pickupChannelId;
        }
        item.fulfillmentInstruction = "PICKUP_INSTORE";
        item.status = "ORDERED";
      });
    }

    return orderObject;
  });

  return { notes, items, order, tip, pickupChannelId, isLegacy: true };
}
